// React
import { ChangeEvent, useEffect, useState } from 'react';
// Mantine
import {
  NumberFormatter,
  Select,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { MRT_ColumnDef, MRT_Row } from 'mantine-react-table';
import { useTranslation } from '@refinedev/core';

// Interface
import type { Site } from 'interfaces/site';
import type { Organization } from 'interfaces/organization';
import type { Payor } from 'interfaces/payor';
import { DataBadge } from 'components/common/DataBadge';
import { StatusBadge } from 'components/common/StatusBadge';
import {
  getChequePaymentOption,
  getCreditCardPaymentOption,
  getETransferPaymentOption,
  SitePaymentOptionEdit,
} from 'pages/sites/components/sitePaymentOptions';
import {
  DEFAULT_SITE_TIME_ZONE,
  TIME_ZONE_OPTIONS,
} from '@billing/validation/timezone';
import {
  isValidFax,
  isValidPostalCode,
  normalizeFaxInput,
  normalizePostalCodeInput,
  siteContactValidationMessages,
  HEALTH_REGION_OPTIONS,
} from '../../validation/site';
import { getCanadianTimeZoneLabel } from 'utils/timezoneUtils';
import { provincesEnum } from '@billing/validation';
import { useSiteValidationState } from 'pages/sites/components/SiteValidationContext';
import { resolveSiteDefaultPayor } from 'store';

const regionLabelByValue = new Map<string, string>(
  HEALTH_REGION_OPTIONS.flatMap(({ items }) =>
    items.map(({ value, label }) => [value, label] as [string, string])
  )
);

const getRegionLabel = (region: string) =>
  regionLabelByValue.get(region) ?? region;

const statusOptions = ['Active', 'Inactive'];

const invoiceeOptions = [
  { value: 'site', label: 'Site (Clinic)' },
  { value: 'provider', label: 'Provider' },
];

const getOrganizationLabel = (
  organizationID: string | null | undefined,
  organizations: Organization[]
) =>
  organizations.find((organization) => organization.id === organizationID)
    ?.name;

const renderBooleanCell = (value: boolean, key?: string) => (
  <StatusBadge active={value} key={key}>
    {value ? 'Yes' : 'No'}
  </StatusBadge>
);

const getRowInvoicee = (
  row: MRT_Row<Site>,
  invoiceeByRowId: Record<string, string | undefined>
) =>
  invoiceeByRowId[row.id] ??
  row._valuesCache.invoicee ??
  row.original.invoicee ??
  '';

const getRowInvoiceRate = (
  row: MRT_Row<Site>,
  invoiceRateByRowId: Record<string, string | undefined>
) =>
  invoiceRateByRowId[row.id] ??
  row._valuesCache.invoiceRate ??
  row.original.invoiceRate ??
  '';

const getShowOverhead = (
  row: MRT_Row<Site>,
  showOverheadByRowId: Record<string, boolean | undefined>,
  invoiceeByRowId: Record<string, string | undefined>
) => {
  const value = showOverheadByRowId[row.id];
  if (value !== undefined) return value;
  return getRowInvoicee(row, invoiceeByRowId) === 'site';
};

const SiteFaxEditField = ({ cell }: { cell: any }) => {
  const { showValidationErrors } = useSiteValidationState();
  const [touched, setTouched] = useState(false);
  const [fax, setFax] = useState<string>((cell.getValue() as string) || '');
  const showFaxError = touched || showValidationErrors;
  const faxError =
    showFaxError && !isValidFax(fax)
      ? siteContactValidationMessages.fax
      : undefined;

  return (
    <TextInput
      label="Fax"
      key={cell.row.id}
      placeholder="Fax"
      data-testid="faxTextInput"
      errorProps={{ 'data-testid': 'faxTextInputError' }}
      required
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={10}
      value={fax}
      error={faxError}
      onChange={(e) => {
        const value = normalizeFaxInput(e.target.value);
        setFax(value);
        cell.row._valuesCache.fax = value;
      }}
      onBlur={() => setTouched(true)}
    />
  );
};

const SitePostalCodeEditField = ({ cell }: { cell: any }) => {
  const { showValidationErrors } = useSiteValidationState();
  const [touched, setTouched] = useState(false);
  const [postalCode, setPostalCode] = useState<string>(
    (cell.getValue() as string) || ''
  );
  const showPostalCodeError = touched || showValidationErrors;
  const postalCodeError =
    showPostalCodeError &&
    postalCode.length > 0 &&
    !isValidPostalCode(postalCode)
      ? siteContactValidationMessages.postalCode
      : undefined;

  return (
    <TextInput
      label="Postal Code"
      key={cell.row.id}
      placeholder="Postal Code"
      data-testid="postalCodeTextInput"
      errorProps={{ 'data-testid': 'postalCodeTextInputError' }}
      value={postalCode}
      maxLength={7}
      autoComplete="postal-code"
      error={postalCodeError}
      onChange={(e) => {
        const value = normalizePostalCodeInput(e.target.value);
        setPostalCode(value);
        cell.row._valuesCache.postalCode = value;
      }}
      onBlur={() => setTouched(true)}
    />
  );
};

const buildGroupedPayorOptions = (
  payors: Payor[],
  groupLabels: Record<Payor['type'], string>
) => {
  const optionsByType = new Map<
    Payor['type'],
    { value: string; label: string }[]
  >();

  payors.forEach((payor) => {
    const options = optionsByType.get(payor.type) ?? [];
    options.push({ value: payor.id, label: payor.name });
    optionsByType.set(payor.type, options);
  });

  return Array.from(optionsByType, ([type, options]) => ({
    group: groupLabels[type],
    items: options.sort((a, b) => a.label.localeCompare(b.label)),
  }));
};

const SiteDefaultPayorEditField = ({
  row,
  payors,
}: {
  row: MRT_Row<Site>;
  payors: Payor[];
}) => {
  const { translate } = useTranslation();
  const { province } = useSiteValidationState();
  const [selectedDefaultPayorID, setSelectedDefaultPayorID] = useState<
    string | null
  >(row.original.settings?.defaultPayorID ?? null);

  const handlePayorChange = (value: string | null) => {
    setSelectedDefaultPayorID(value);
    row._valuesCache.settings = {
      ...(row.original.settings ?? {}),
      defaultPayorID: value,
    };
  };

  const resolvedDefaultPayorID =
    resolveSiteDefaultPayor(
      {
        province,
        settings: { defaultPayorID: selectedDefaultPayorID },
      },
      payors
    )?.id || null;

  useEffect(() => {
    row._valuesCache.settings = {
      ...(row._valuesCache.settings ?? row.original.settings ?? {}),
      defaultPayorID: resolvedDefaultPayorID,
    };
  }, [resolvedDefaultPayorID, row]);

  const groupedPayorOptions = buildGroupedPayorOptions(payors, {
    DIRECT: translate('pages:sites.payorGroups.direct'),
    PRIVATE: translate('pages:sites.payorGroups.private'),
    PUBLIC: translate('pages:sites.payorGroups.public'),
  });

  return (
    <Select
      label={translate('pages:sites.fields.defaultPayor')}
      placeholder={translate('pages:sites.fields.defaultPayorPlaceholder')}
      data={groupedPayorOptions}
      value={resolvedDefaultPayorID}
      onChange={handlePayorChange}
      searchable
      allowDeselect={false}
      data-testid="siteDefaultPayorSelect"
    />
  );
};

const SiteProvinceEditField = ({ row }: { row: MRT_Row<Site> }) => {
  const { translate } = useTranslation();
  const { province, setProvince } = useSiteValidationState();

  const handleProvinceChange = (value: string | null) => {
    const result = provincesEnum.safeParse(value);
    const selectedProvince = result.success ? result.data : null;

    setProvince(selectedProvince);
    row._valuesCache.province = selectedProvince ?? '';
  };

  return (
    <Select
      label={translate('pages:sites.fields.province')}
      placeholder={translate('pages:sites.fields.province')}
      data-testid="ProvinceInput"
      errorProps={{ 'data-testid': 'provinceInputError' }}
      value={province}
      data={provincesEnum.options}
      onChange={handleProvinceChange}
    />
  );
};

export const SiteColumns = (
  organizationProp: Organization[],
  isAdmin: boolean,
  payors: Payor[] = []
) => {
  const { translate } = useTranslation();
  const [invoiceeByRowId, setInvoiceeByRowId] = useState<
    Record<string, string | undefined>
  >({});
  const [invoiceRateByRowId, setInvoiceRateByRowId] = useState<
    Record<string, string | undefined>
  >({});
  const [showOverheadByRowId, setShowOverheadByRowId] = useState<
    Record<string, boolean | undefined>
  >({});
  const etransferPaymentOption = getETransferPaymentOption(translate);
  const creditCardPaymentOption = getCreditCardPaymentOption(translate);
  const chequePaymentOption = getChequePaymentOption(translate);

  const columns: MRT_ColumnDef<Site>[] = [
    {
      accessorKey: 'id',
      accessorFn: (row) => row.id,
      header: 'ID',
      enableEditing: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      Edit: ({ cell }) => {
        const [name, setName] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Name"
            key={cell.row.id}
            placeholder="Name"
            data-testid="siteNameInput"
            required
            errorProps={{ 'data-testid': 'nameTextInputError' }}
            value={name}
            onChange={(e) => {
              const value = e.target.value;
              setName(value);
              cell.row._valuesCache.name = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.name,
      size: 50,
    },
    {
      accessorKey: 'address',
      header: 'Address',
      Edit: ({ cell }) => {
        const [address, setAddress] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Address"
            key={cell.row.id}
            placeholder="Address"
            data-testid="addressTextInput"
            required
            errorProps={{ 'data-testid': 'addressTextInputError' }}
            value={address}
            onChange={(e) => {
              const value = e.target.value;
              setAddress(value);
              cell.row._valuesCache.address = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.address,
      size: 50,
    },
    {
      accessorKey: 'region',
      header: 'Region',
      mantineEditTextInputProps: {
        required: true,
      },
      Edit: ({ cell }) => {
        const [region, setRegion] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <Select
            label="Region"
            data={HEALTH_REGION_OPTIONS}
            data-testid="regionSelect"
            value={region}
            required
            searchable
            allowDeselect={false}
            placeholder="Select region"
            nothingFoundMessage="No regions found"
            errorProps={{ 'data-testid': 'regionSelectError' }}
            onChange={(value) => {
              if (!value) return;
              setRegion(value);
              cell.row._valuesCache.region = value;
            }}
          />
        );
      },
      Cell: ({ cell }) => (
        <DataBadge key={cell.id}>
          {getRegionLabel(cell.getValue() as string)}
        </DataBadge>
      ),
    },
    {
      accessorKey: 'timezone',
      header: 'Timezone',
      Edit: ({ row }) => {
        const [timezone, setTimezone] = useState<string>(
          (row.original.timezone as string) || DEFAULT_SITE_TIME_ZONE
        );
        row._valuesCache.timezone = timezone;

        return (
          <Select
            label="Timezone"
            data={[...TIME_ZONE_OPTIONS]
              .sort((a, b) => a.localeCompare(b))
              .map((key) => ({
                value: key,
                label: getCanadianTimeZoneLabel(key),
              }))}
            placeholder="Select timezone"
            data-testid="timezoneSelect"
            value={timezone}
            searchable
            allowDeselect={false}
            nothingFoundMessage="No timezones found"
            onChange={(value) => {
              if (!value) return;
              setTimezone(value);
              row._valuesCache.timezone = value;
            }}
          />
        );
      },
      accessorFn: (row) => getCanadianTimeZoneLabel(row.timezone),
      size: 50,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      Edit: ({ cell }) => {
        const [email, setEmail] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Email"
            key={cell.row.id}
            placeholder="Email"
            data-testid="emailTextInput"
            value={email}
            onChange={(e) => {
              const value = e.target.value;
              setEmail(value);
              cell.row._valuesCache.email = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.email,
      size: 50,
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      Edit: ({ cell }) => {
        const [phone, setPhone] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Phone"
            key={cell.row.id}
            placeholder="Phone"
            data-testid="phoneTextInput"
            value={phone}
            onChange={(e) => {
              const value = e.target.value;
              setPhone(value);
              cell.row._valuesCache.phone = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.phone,
      size: 50,
    },
    {
      accessorKey: 'fax',
      header: 'Fax',
      Edit: ({ cell }) => <SiteFaxEditField cell={cell} />,
      size: 50,
    },
    {
      accessorKey: 'postalCode',
      header: 'Postal Code',
      accessorFn: (row) => row.postalCode,
      Edit: ({ cell }) => <SitePostalCodeEditField cell={cell} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      mantineEditTextInputProps: {
        required: true,
      },
      Edit: ({ cell }) => {
        const [status, setStatus] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <Select
            label="Status"
            data={statusOptions}
            data-testid="statusSelect"
            value={status}
            onChange={(value) => {
              if (!value) return;
              setStatus(value);
              cell.row._valuesCache.status = value;
            }}
          />
        );
      },
      Cell: ({ cell }) => (
        <StatusBadge key={cell.id}>{cell.getValue() as string}</StatusBadge>
      ),
      size: 50,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      Edit: ({ cell }) => {
        const [description, setDescription] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label={translate(
              'pages:sites.fields.descriptionOptional',
              'Description (optional)'
            )}
            placeholder={translate(
              'pages:sites.fields.description',
              'Description'
            )}
            data-testid="descriptionTextInput"
            value={description}
            onChange={(e) => {
              const value = e.target.value;
              setDescription(value);
              cell.row._valuesCache.description = value;
            }}
          />
        );
      },
    },
    {
      id: 'defaultPayorID',
      accessorFn: (row) => row.settings?.defaultPayorID,
      header: translate('pages:sites.fields.defaultPayor'),
      Edit: ({ row }) => (
        <SiteDefaultPayorEditField row={row} payors={payors} />
      ),
      Cell: ({ cell }) => {
        const defaultPayorID = cell.getValue<string | null>();
        const defaultPayorName = payors.find(
          ({ id }) => id === defaultPayorID
        )?.name;

        return <DataBadge>{defaultPayorName ?? ''}</DataBadge>;
      },
    },
    {
      accessorKey: 'proFees',
      header: 'Pro fees',
      mantineEditTextInputProps: {
        required: true,
      },
      Edit: ({ cell }) => {
        const [checked, setChecked] = useState<boolean>(
          Boolean((cell.getValue() as boolean) || false)
        );

        cell.row._valuesCache.proFees = checked;

        return (
          <Switch
            label="Pro fees"
            description={translate(
              'pages:sites.descriptions.proFees',
              'This option allows the site to add professional fees to claims'
            )}
            checked={checked}
            data-testid="proFeesToggle"
            onChange={(e) => {
              const value = e.target.checked;
              setChecked(value);
              cell.row._valuesCache.proFees = value;
            }}
          />
        );
      },
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue()), cell.id),
      size: 50,
    },
    {
      accessorKey: 'allowUploads',
      header: translate('pages:sites.fields.allowUploads', 'Uploads Enabled'),
      Edit: ({ cell }) => {
        const [checked, setChecked] = useState<boolean>(
          (cell.getValue() as boolean | undefined) ?? true
        );
        cell.row._valuesCache.allowUploads = checked;
        return (
          <Switch
            label={translate(
              'pages:sites.fields.allowUploads',
              'Uploads Enabled'
            )}
            description={translate(
              'pages:sites.descriptions.allowUploads',
              'This option allows the site to upload documents and files'
            )}
            checked={checked}
            data-testid="uploadsEnabledToggle"
            onChange={(e) => {
              const value = e.target.checked;
              setChecked(value);
              cell.row._valuesCache.allowUploads = value;
            }}
          />
        );
      },
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue()), cell.id),
      size: 50,
    },
    {
      accessorKey: 'techFees',
      header: 'Technical fees',
      mantineEditTextInputProps: {
        required: true,
      },
      Edit: ({ cell }) => {
        const [checked, setChecked] = useState<boolean>(
          Boolean((cell.getValue() as boolean) || false)
        );

        cell.row._valuesCache.techFees = checked;

        return (
          <Switch
            label="Technical fees"
            description={translate(
              'pages:sites.descriptions.techFees',
              'This option allows the site to add technical fees to claims'
            )}
            checked={checked}
            data-testid="techFeesToggle"
            onChange={(e) => {
              const value = e.target.checked;
              setChecked(value);
              cell.row._valuesCache.techFees = value;
            }}
          />
        );
      },
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue()), cell.id),
      size: 50,
    },
    {
      accessorKey: 'organizationID',
      header: 'Organization',
      editVariant: 'select',
      mantineEditTextInputProps: {
        required: true,
        errorProps: { 'data-testid': 'organizationTextInputError' },
      },
      Edit: ({ cell }) => {
        const [organizationID, setOrganizationID] = useState<string>(
          ((cell.getValue() as string) || organizationProp[0]?.id) ?? ''
        );

        cell.row._valuesCache.organizationID = organizationID;

        return isAdmin ? (
          <Select
            label="Organization"
            data={organizationProp.map((organization) => ({
              value: organization.id,
              label: organization.name,
            }))}
            value={organizationID}
            onChange={(value) => {
              if (!value) return;
              setOrganizationID(value);
              cell.row._valuesCache.organizationID = value;
            }}
            data-testid="organizationSelect"
            placeholder="Select organizations"
          />
        ) : (
          <TextInput
            label="Organization"
            value={getOrganizationLabel(
              cell.getValue<string>(),
              organizationProp
            )}
            data-testid="organizationReadOnlyInput"
            readOnly
            disabled
          />
        );
      },
      Cell: ({ cell }) => (
        <DataBadge key={cell.id}>
          {getOrganizationLabel(cell.getValue<string>(), organizationProp)}
        </DataBadge>
      ),
    },
    {
      accessorKey: 'etransfer',
      header: 'E-transfer',
      Edit: ({ cell }) => (
        <SitePaymentOptionEdit cell={cell} config={etransferPaymentOption} />
      ),
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue())),
      size: 50,
    },
    {
      accessorKey: 'creditCard',
      header: 'Credit card',
      Edit: ({ cell }) => (
        <SitePaymentOptionEdit cell={cell} config={creditCardPaymentOption} />
      ),
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue())),
      size: 50,
    },
    {
      accessorKey: 'cheque',
      header: 'Cheque',
      Edit: ({ cell }) => (
        <SitePaymentOptionEdit cell={cell} config={chequePaymentOption} />
      ),
      Cell: ({ cell }) => renderBooleanCell(Boolean(cell.getValue())),
      size: 50,
    },
    {
      accessorKey: 'invoicee',
      header: 'Invoicee',
      size: 50,
      Edit: ({ cell, row }) => {
        const [invoiceeValue, setInvoiceeValue] = useState<string>(
          getRowInvoicee(row, invoiceeByRowId)
        );

        return (
          <Tooltip label="Choose where invoices will be sent." withArrow>
            <Select
              label="Invoicee"
              data={invoiceeOptions}
              placeholder="Select Invoicee"
              value={invoiceeValue}
              onChange={(value) => {
                if (!value) return;
                setInvoiceeValue(value);
                setInvoiceeByRowId((current) => ({
                  ...current,
                  [row.id]: value,
                }));
                cell.row._valuesCache.invoicee = value;

                if (value === 'provider') {
                  row._valuesCache.overhead = getRowInvoiceRate(
                    row,
                    invoiceRateByRowId
                  );
                  setShowOverheadByRowId((current) => ({
                    ...current,
                    [row.id]: false,
                  }));
                  return;
                }

                setShowOverheadByRowId((current) => ({
                  ...current,
                  [row.id]: true,
                }));
              }}
              data-testid="invoiceeSelect"
              allowDeselect={false}
            />
          </Tooltip>
        );
      },
      Cell: ({ cell }) => <DataBadge>{cell.getValue<string>()}</DataBadge>,
    },
    {
      accessorKey: 'overhead',
      header: 'Overhead (%)',
      size: 50,
      Edit: ({ cell }) => {
        const showOverhead = getShowOverhead(
          cell.row,
          showOverheadByRowId,
          invoiceeByRowId
        );
        const invoiceRateValue = getRowInvoiceRate(
          cell.row,
          invoiceRateByRowId
        );
        const [overhead, setOverhead] = useState<string>(
          String(cell.row._valuesCache.overhead ?? cell.getValue() ?? '')
        );

        useEffect(() => {
          if (!showOverhead) {
            setOverhead(invoiceRateValue);
            cell.row._valuesCache.overhead = Number(invoiceRateValue);
          }
        }, [cell.row, invoiceRateValue, showOverhead]);

        if (!showOverhead) {
          return null;
        }

        return (
          <TextInput
            label="Overhead (%)"
            placeholder="Enter overhead percentage"
            data-testid="overheadTextInput"
            value={overhead}
            type="number"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              setOverhead(value);
              cell.row._valuesCache.overhead = Number(value);
            }}
          />
        );
      },
      Cell: ({ cell }) => (
        <NumberFormatter value={cell.getValue<number>()} suffix="%" />
      ),
    },
    {
      accessorKey: 'invoiceRate',
      header: 'Invoice Rate (%)',
      size: 50,
      Edit: ({ cell, row }) => {
        const showOverhead = getShowOverhead(
          row,
          showOverheadByRowId,
          invoiceeByRowId
        );
        const [invoiceRate, setInvoiceRate] = useState<string>(
          getRowInvoiceRate(row, invoiceRateByRowId)
        );

        useEffect(() => {
          if (!showOverhead) {
            row._valuesCache.overhead = Number(invoiceRate);
          }
        }, [invoiceRate, row, showOverhead]);

        return (
          <Tooltip label="Amount we charge per invoice." withArrow>
            <TextInput
              label="Invoice Rate (%)"
              placeholder="Enter invoice rate percentage"
              data-testid="invoiceRateTextInput"
              required
              type="number"
              step="0.01"
              value={invoiceRate}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setInvoiceRate(value);
                setInvoiceRateByRowId((current) => ({
                  ...current,
                  [row.id]: value,
                }));
                cell.row._valuesCache.invoiceRate = Number(value);
              }}
            />
          </Tooltip>
        );
      },
      Cell: ({ cell }) => (
        <NumberFormatter value={cell.getValue<number>()} suffix="%" />
      ),
    },
    {
      accessorKey: 'apiID',
      header: 'API ID',
      Edit: ({ cell }) => {
        const [apiID, setApiID] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="API ID"
            key={cell.row.id}
            placeholder="API ID"
            data-testid="siteAPIIDInput"
            errorProps={{ 'data-testid': 'nameTextInputError' }}
            value={apiID}
            onChange={(e) => {
              const value = e.target.value;
              setApiID(value);
              cell.row._valuesCache.apiID = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.apiID,
      size: 50,
    },
    {
      accessorKey: 'apiSecret',
      header: 'API Secret',
      Edit: ({ cell }) => {
        const [apiSecret, setApiSecret] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="API Secret"
            key={cell.row.id}
            placeholder="API Secret"
            data-testid="siteAPISecretInput"
            errorProps={{ 'data-testid': 'nameTextInputError' }}
            value={apiSecret}
            onChange={(e) => {
              const value = e.target.value;
              setApiSecret(value);
              cell.row._valuesCache.apiSecret = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.apiSecret,
      size: 50,
    },
    {
      accessorKey: 'apiKey',
      header: 'API Key',
      Edit: ({ cell }) => {
        const [apiKey, setApiKey] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="API Key"
            key={cell.row.id}
            placeholder="API Key"
            data-testid="siteAPIKeyInput"
            errorProps={{ 'data-testid': 'apiKeyInputError' }}
            value={apiKey}
            onChange={(e) => {
              const value = e.target.value;
              setApiKey(value);
              cell.row._valuesCache.apiKey = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.apiKey,
      size: 50,
    },
    {
      accessorKey: 'hospitalCode',
      header: 'Hospital Code',
      Edit: ({ cell }) => {
        const [hospitalCode, setHospitalCode] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Hospital Code"
            key={cell.row.id}
            placeholder="Hospital Code"
            data-testid="hospitalCodeInput"
            errorProps={{ 'data-testid': 'hospitalCodeInputError' }}
            value={hospitalCode}
            onChange={(e) => {
              const value = e.target.value;
              setHospitalCode(value);
              cell.row._valuesCache.hospitalCode = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.hospitalCode,
      size: 50,
    },
    {
      accessorKey: 'siteNumber',
      header: 'Site Number',
      Edit: ({ cell }) => {
        const [siteNumber, setSiteNumber] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="Site Number"
            key={cell.row.id}
            placeholder="Site Number"
            data-testid="SiteNumberInput"
            errorProps={{ 'data-testid': 'siteNumberInputError' }}
            value={siteNumber}
            onChange={(e) => {
              const value = e.target.value;
              setSiteNumber(value);
              cell.row._valuesCache.siteNumber = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.siteNumber,
      size: 50,
    },
    {
      accessorKey: 'province',
      header: 'Province',
      Edit: ({ row }) => <SiteProvinceEditField row={row} />,
      accessorFn: (row) => row.province,
      size: 50,
    },
    {
      accessorKey: 'city',
      header: 'City',
      Edit: ({ cell }) => {
        const [city, setCity] = useState<string>(
          (cell.getValue() as string) || ''
        );

        return (
          <TextInput
            label="City"
            key={cell.row.id}
            placeholder="City"
            data-testid="cityInput"
            errorProps={{ 'data-testid': 'cityInputError' }}
            value={city}
            onChange={(e) => {
              const value = e.target.value;
              setCity(value);
              cell.row._valuesCache.city = value;
            }}
          />
        );
      },
      accessorFn: (row) => row.city,
      size: 50,
    },
  ];
  return columns;
};
